import { handleRefHandlers } from "@utils/general";
import { KeyName } from "keyName";
import { debounce } from "lodash";

import { IDomProps, IInputPropsExtended } from "../Input";


export type InputRefs = {
    inputRef: React.MutableRefObject<HTMLInputElement>
    wasChangedSinceBlurRef: React.MutableRefObject<boolean>
    wrapperRef: React.MutableRefObject<HTMLElement>
}

export type UseInputEventHandlers = { inputProps: IInputPropsExtended } & InputRefs 
// TODO: might be better to use only relevant props instead of whole props of Input
export const useInputEventHandlers = ({inputProps, ...props}: UseInputEventHandlers) => {

    const debouncedOnChange = debounce((e: React.ChangeEvent<HTMLInputElement>) => {
        inputProps?.onChange({
            value: e.target.value,
            origEvent: e,
            triggerAdditionalTasks: true,
        });
    }, inputProps.debouncedWait);

    const handleRowsChange = (firstTime = false): void => {
        inputProps.domManipulatorOrchestrator.registerCallback<IDomProps>(
            () => {
                if (props.inputRef.current) {
                    return null;
                }

                const normalLineHeight = 1.2;
                const inputStyle = getComputedStyle(props.inputRef.current);
                const lineHeight = Math.round(parseInt(inputStyle.fontSize) * normalLineHeight);
                const inputHeight = props.inputRef.current.clientHeight;
                const numRows = Math.round(inputHeight / lineHeight);
                return {
                    numRows: numRows,
                    newHeight: inputHeight + lineHeight,
                };
            },
            (data) => {
                if (!firstTime || data?.numRows > 1) {
                    setTimeout(() => {
                        inputProps.onRowsChange?.({
                            numRows: data.numRows,
                            newHeight: `${data.newHeight}px`,
                        });
                    }, 0);
                }
            },
            [props.inputRef]
        );
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
        inputProps.onKeyDown?.(e);
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>): void => {
        inputProps.onKeyPress?.(e);
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>): void => {
        if (inputProps.selectOnFocus) {
            props.inputRef.current?.setSelectionRange(0, inputProps.value?.length ?? 0);
        }
        inputProps.onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>): void => {
        inputProps.onBlur?.({
            origEvent: e,
            wasChanged: props.wasChangedSinceBlurRef.current,
        });

        debouncedOnChange?.flush();

        props.wasChangedSinceBlurRef.current = false;
    };

    const handleOnIconClick = (event: React.MouseEvent): void => {
        inputProps.onIconClick?.(event);
    };

    const handleInputRef = (ref: HTMLInputElement): void => {
        handleRefHandlers(ref, props.inputRef, inputProps.passRef);
    };
    
    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        inputProps?.onChange({
            value: e.target.value,
            origEvent: e,
        });

        if (inputProps.debouncedWait) {
            debouncedOnChange(e);
        }
    };

    const handleInputClick = (event: React.MouseEvent): void => {
        if (inputProps.isSynchronized || inputProps.isDisabled) {
            return;
        }

        props.inputRef.current.focus();
        inputProps.onClick && inputProps.onClick(event);
    };

    const handleMouseDown = (event: any): void => {
        // this prevents firing blur event on input when user still clicks inside wrapper (which is from user perspective
        // still input)
        if (
            props.wrapperRef.current.contains(event.target) &&
            props.inputRef.current !== event.target &&
            document.activeElement === props.inputRef.current
        ) {
            event.preventDefault();
        }
    };

    const handleFakeInputKeyDown = (e: React.KeyboardEvent): void => {
        if (e.key === KeyName.Enter) {
            inputProps.onIconClick?.(e as any);
        }
    };

    const handleAutosizeResize = (): void => {
        handleRowsChange();
    };

    return {
        handleRowsChange,
        handleKeyDown,
        handleKeyPress,
        handleFocus,
        handleBlur,
        handleOnIconClick,
        handleInputRef,
        handleMouseDown,
        handleFakeInputKeyDown,
        handleOnChange,
        handleInputClick,
        handleAutosizeResize
    };
};